import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import AdvancedTable from 'components/AdvancedTable'
import { useTranslation } from 'react-i18next'
import type { PrizeGroup } from 'types/Prize'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAppContext } from 'context/AppContext/AppContext'
import RequestStatus from 'components/RequestStatus/RequestStatus'
import { AbilityContext, type AbilityType, Can } from 'config/ability'
// import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined'

import { useAuthContext } from 'context/AuthContext/AuthContext'
import { useAbility } from '@casl/react'
import { type PureAbility } from '@casl/ability'
import { PrizeGroupRow } from './features/prize-group-row'
import { Add } from '@mui/icons-material'
import { ExportIcon } from 'assets/icons/export'
import { Market } from 'types/Market'
import { Divider } from '@mui/material'

const PrizesGroups = ({ market }: { market?: Market }): JSX.Element => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const ability = useAbility<PureAbility<AbilityType>>(AbilityContext)
  const { t } = useTranslation()
  const { markets } = useAppContext()
  const { token } = useAuthContext()

  const marketFilter = market && market.id !== -1 && !!market?.id ? `?marketId=${market.id}` : ''

  const handleDownloadPrizeGroups = async (): Promise<any> => {
    const url = `${process.env.REACT_APP_API_URL}/prizes/groups/export${marketFilter}`

    const options = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }

    const res = await fetch(url, options)
    if (res.ok) {
      const blob = await res.blob()
      const file = window.URL.createObjectURL(blob)

      window.location.assign(file)
    }
    void queryClient.refetchQueries([`prizes-groups-${marketFilter}`])
  }

  if (markets === undefined) {
    return <RequestStatus data={markets} />
  }

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="flex-end">
        {/* <Can I="create" a="Prizes">
          <Button
            sx={{ mr: 1 }}
            variant="outlined"
            color="secondary"
            onClick={(): void => {
              navigate('/prizes')
            }}
            startIcon={<ViewTimelineOutlinedIcon />}
          >
            {t('prize.showAll')}
          </Button>
        </Can> */}
        <Can I="create" a="Prizes">
          <Button
            variant="outlined"
            color="secondary"
            sx={{ mr: 1 }}
            onClick={(): void => {
              navigate('/prizes/add')
            }}
            startIcon={<Add />}
          >
            {t('common.add')}
          </Button>
        </Can>
        <Can I="download" a="Prizes">
          <Button onClick={handleDownloadPrizeGroups} type="button" variant="contained" startIcon={ExportIcon}>
            {t('common.exportList')}
          </Button>
        </Can>
      </Box>
      <Divider sx={{ my: 4 }} />
      <AdvancedTable<PrizeGroup>
        key={`prizes-groups-${marketFilter}`}
        name={`prizes-groups-${marketFilter}`}
        disableFilters
        endpoint={`prizes/groups${marketFilter}`}
        headings={[
          { type: 'empty', name: 'ID', field: 'id', sort: true, width: 90 },
          { type: 'empty', name: t('common.prizeName'), field: 'name' },
          { type: 'empty', name: t('common.embargoDate'), field: 'embargoDate' },
          { type: 'empty', name: t('common.market'), field: '', width: 120 },
          { type: 'empty', name: t('common.availability'), field: '', width: 120 },
          { type: 'empty', name: t('common.type'), field: '', width: 120 },
          { type: 'empty', name: t('common.marketName'), field: 'marketId', width: 120 },
          { type: 'empty', name: t('common.createdAt'), field: 'createdAt', width: 160 },
          {
            type: 'empty',
            name: '',
            field: '',
            width: 120,
            hide: ability.cannot('update', 'Prizes') && ability.cannot('delete', 'Prizes'),
          },
        ]}
        options={{}}
        renderRow={(row, idx) => (
          <PrizeGroupRow
            key={`prize-group-${idx}-${row.id}`}
            {...row}
            idx={idx}
            queryKey={`prizes-groups-${marketFilter}-query`}
          />
        )}
      />
    </>
  )
}

export default PrizesGroups
